'use client'

import Cookies from 'js-cookie'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaLock, FaMagic } from 'react-icons/fa'
import Modal from '../../component/Modal'
import { useAuth } from '../../contexts/AuthContext'
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../../helpers/safeStorage'
// Static import (not next/dynamic ssr:false) so the feature/FAQ copy lands in server HTML for
// crawlers. A dynamic import suspends to the page's Suspense fallback during SSR; a plain import
// renders inline. The image grid is client-only by nature (filteredImages is set in a useEffect),
// so on the server it renders its empty branch — copy is crawlable, images hydrate on the client.
import AiGeneratorLandingBottomHalf from './AIGeneratorLandingBottomHalf'

const AiGeneratorLanding = () => {
  const { t } = useTranslation()

  const { user } = useAuth()
  const router = useRouter()

  const [welcomeModal, setWelcomeModal] = useState(false)
  const [upgradeModal, setUpgradeModal] = useState(false)
  const [safeWarningOpen, setSafeWarningOpen] = useState(false)

  const videoRef = useRef(null)

  useEffect(() => {
    safeSetItem('AiGenerator', 'true')
  }, [])

  useEffect(() => {
    const playVideo = async () => {
      try {
        if (videoRef.current === null) return
        await videoRef.current.play()
      } catch (error) {
        console.error('Autoplay failed:', error)
      }
    }

    playVideo()
  }, [])

  useEffect(() => {
    const welcomeBack =
      safeGetItem('welcomeBack100v8') || Cookies.get('welcomeBack100v8')
    if (welcomeBack === 'true') {
      setWelcomeModal(true)
    }
  }, [])

  const claimWelcomeGems = async () => {
    if (typeof window !== 'undefined') return
    try {
      safeRemoveItem('welcomeBack100v8')
      Cookies.remove('welcomeBack100v8')

      const idToken = await user.getIdToken()

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/claim_welcome_reward`,
        {
          method: 'POST',
          headers: {
            Authorization: idToken,
            'Content-Type': 'application/json',
          },
        },
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong')
      }

      router.refresh()
    } catch {
      // console.log(err)
    }
    setWelcomeModal(false)
  }

  return (
    <div className="flex-grow md:pt-22 pt-32">
      <div className="bg-[var(--background-bg-color)] min-h-screen">
        <Modal
          isOpen={welcomeModal}
          closeModal={() => setWelcomeModal(false)}
          title="Happy Easter 🐣!"
          content="Win up to 1,000 Gems, and test out our amazing new features for free. 1 day left!"
          confirmation="Claim Prize!"
          onConfirm={user ? claimWelcomeGems : () => router.push('/signup')}
        />

        <Modal
          isOpen={upgradeModal}
          closeModal={() => setUpgradeModal(false)}
          title={
            <div className="flex items-center">
              <FaLock className="text-white opacity-70 z-20 mr-2" />
              <p>NSFW Content</p>
            </div>
          }
          content="You must sign up to view NSFW content."
          confirmation="Sign Up"
          onConfirm={() => router.push('/signup')}
        />

        <Modal
          isOpen={safeWarningOpen}
          closeModal={() => {
            setSafeWarningOpen(false)
          }}
          title={'Safe Mode Enabled'}
          content={
            <div>
              <p className="text-white">
                This feature may contain mature themes. Please disable Safe Mode
                in Settings for access.
              </p>
            </div>
          }
          confirmation="Settings"
          onConfirm={() => router.push('/settings')}
        />

        <div className="mx-auto w-full px-5 sm:px-0 sm:w-3/4 md:mt-14">
          <div className="min-h-screen">
            <div className="md:flex items-center justify-center space-x-3">
              <Link href="/signup" aria-label="Sign up for Promptchan">
                <div>
                  <div className="">
                    <video
                      // ref={videoRef}
                      autoPlay="autoplay"
                      loop={true}
                      muted={true}
                      playsInline
                      preload="metadata"
                      poster={
                        typeof window !== 'undefined' &&
                        window.innerWidth <= 768
                          ? '/assets/thumbnailMobile.webp'
                          : '/assets/thumbnailDesktop.webp'
                      }
                    >
                      <source
                        src={
                          typeof window !== 'undefined' &&
                          window.innerWidth <= 768
                            ? '/assets/landingVideoMobileSafe.webm'
                            : '/assets/landingVideoSafe.webm'
                        }
                      />
                    </video>
                  </div>
                </div>
              </Link>

              <div className="mt-6 md:mt-0 md:w-2/3 ">
                <h1 className="md:text-6xl text-5xl font-semibold tracking-tight text-white text-center mb-6">
                  Promptchan - AI Image Generator
                </h1>
                <p className="md:text-xl text-sm font-light tracking-tight text-gray-300 text-center mx-auto">
                  Experience the limits of creativity with the full potential of
                  AI image generation.
                </p>

                <div className="md:mt-4 flex flex-col items-center justify-center rounded-xl">
                  <Link href="/signup" aria-label="Sign up for Promptchan">
                    <div className="flex items-center justify-center px-6 w-72 max-w-full md:py-5 py-4 my-4 bg-gradient-to-r from-[#4f46e5] via-[#7F86FF] to-[#4f46e5] text-white rounded-3xl focus:outline-none ml-auto mr-auto font-semibold shadow-lg text-lg text-center transition-transform duration-200 hover:scale-[1.03] ">
                      <p
                        style={{ textShadow: '2px 2px 28px rgba(0, 0, 0, 1)' }}
                      >
                        {t('getStartedFor')}
                      </p>
                      <FaMagic className="ml-3 text-2xl" />
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <button
              className="transition-all duration-300 hover:scale-105 mx-auto flex mt-6 flex flex-col items-center"
              aria-label="Scroll down to learn more"
              onClick={() => {
                window.scrollBy({
                  top: window.innerHeight, // Scrolls down by one viewport height
                  behavior: 'smooth', // Ensures the scrolling is smooth
                })
              }}
            >
              <p className="text-gray-300 font-semibold text-sm mb-3">
                Learn More
              </p>

              <FaChevronDown className=" text-4xl text-gray-300 " />
            </button>
          </div>

          {/* Bottom half here */}
          <AiGeneratorLandingBottomHalf
            setSafeWarningOpen={setSafeWarningOpen}
            upgradeModal={upgradeModal}
            setUpgradeModal={setUpgradeModal}
          />
        </div>
      </div>
    </div>
  )
}

export default AiGeneratorLanding
